import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { SendObligationToOrganizationModal } from '@client/components/obligations/SendObligationToOrganizationModal';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { ICONS } from '@client/styles/theme';
import { type RouterInputs, trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton, Tooltip } from 'antd';
import { Bot, FileSearch } from 'lucide-react';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { masterObligationFormStore } from './store';
import type { MasterObligation } from './types';
import { VersionSelector } from './VersionSelector';

type UpdateInput = RouterInputs['admin']['masterObligation']['updateMasterObligation']['set'];

export const Route = createFileRoute('/admin-console/platform-content/obligations/library/details/$id')({
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    staticData: {
        ui: {
            widthVariant: 'default',
        },
    },
    beforeLoad({ context: { trpcUtils }, params }): ShareableRouteContext {
        async function title() {
            const obligation = await trpcUtils.admin.masterObligation.getMasterObligation.ensureData({
                id: params.id,
            });
            return (
                <span>
                    Obligation Detail • {obligation.id}
                    {obligation.identificationCode ? ` • ${obligation.identificationCode}` : ''}
                </span>
            );
        }

        async function breadcrumb() {
            const obligation = await trpcUtils.admin.masterObligation.getMasterObligation.ensureData({
                id: params.id,
            });
            return `Obligation ${obligation.id}`;
        }

        return {
            ui: {
                title,
            },
            breadcrumb: {
                title: breadcrumb,
            },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <MasterObligationItemDetails />
        </PageController>
    );
}

function MasterObligationItemDetails() {
    const { id } = Route.useParams();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const { mutateAsync: updateMasterObligation } = trpc.admin.masterObligation.updateMasterObligation.useMutation();

    const [isModalVisible, setIsModalVisible] = useState(false);

    // Fetch master obligation with tRPC query
    const obligationQuery = trpc.admin.masterObligation.getMasterObligation.useQuery(
        { id },
        {
            enabled: !!id,
            throwOnError(error) {
                message.destroy();

                match(error.data?.errorId)
                    .with('recordNotFound', () => {
                        void errorMessage
                            .showAsync("The obligation you are trying to access either doesn't exist.")
                            .then(() => {
                                navigate({ to: '/admin-console/platform-content/obligations/library' });
                            });
                    })
                    .otherwise(() => {
                        errorMessage.show(error);
                    });

                return false;
            },
        },
    );

    const { formData: obligation } = masterObligationFormStore.useInit({
        entity: 'obligation',
        data: obligationQuery.data,
        isLoading: obligationQuery.isLoading,

        async onReload() {
            await obligationQuery.refetch();
        },

        async onSave(currentValue, newValue) {
            try {
                const { controlIds, ...rest } = newValue as MasterObligation & { controlIds?: bigint[] };

                const customLabelUpdates: { [key: string]: string | string[] } = {};

                Object.keys(rest).forEach((key) => {
                    if (key.startsWith('customLabels.')) {
                        const labelId = key.split('.')[1];
                        customLabelUpdates[labelId] = rest[key as keyof typeof rest] as string | string[];
                    }
                });

                const updateData: UpdateInput = {};

                if (Object.keys(customLabelUpdates).length) {
                    updateData.customLabels = {
                        ...currentValue.customLabels,
                        ...customLabelUpdates,
                    };
                }

                if (controlIds?.length) {
                    updateData.controlIds = controlIds;
                }

                if (Object.keys(updateData).length) {
                    await updateMasterObligation({
                        id: currentValue.id!,
                        set: updateData,
                    });
                }

                await trpcUtils.admin.masterObligation.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    // Latest-version gate for the Send button. Shares the tRPC cache with VersionSelector's
    // internal query (same input), so no extra network round-trip. Defaults to "latest" while the
    // query is loading or when the family has a single version, to avoid falsely blocking the
    // happy path. Uses obligationQuery.data for the version because the formData mirror is
    // partial and doesn't expose `version` directly.
    const currentVersion = obligationQuery.data?.version;
    const versionsQuery = trpc.admin.masterObligation.listMasterObligationVersions.useQuery(
        { identificationCode: obligationQuery.data?.identificationCode ?? '' },
        { enabled: Boolean(obligationQuery.data?.identificationCode) },
    );
    const isCurrentLatest =
        !versionsQuery.data ||
        versionsQuery.data.rows.length <= 1 ||
        currentVersion === undefined ||
        currentVersion === Math.max(...versionsQuery.data.rows.map((row) => row.version));

    return obligation && id === obligation.id ? (
        <>
            <SendObligationToOrganizationModal
                open={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                obligationIds={[BigInt(id)]}
                title="Send obligation to organisation"
            />

            <div className="mb-4">
                <VersionSelector currentId={id} identificationCode={obligation.identificationCode ?? null} />
            </div>

            <Tabs
                items={[
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.manage size={16} />
                                Manage obligation
                            </span>
                        ),
                        to: '/admin-console/platform-content/obligations/library/details/$id/manage',
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <Bot size={16} />
                                AI Related
                            </span>
                        ),
                        to: '/admin-console/platform-content/obligations/library/details/$id/ai-related',
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <FileSearch size={16} />
                                Document analyser
                            </span>
                        ),
                        to: '/admin-console/platform-content/obligations/library/details/$id/document-analyser',
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.history size={16} />
                                History
                            </span>
                        ),
                        to: '/admin-console/platform-content/obligations/library/details/$id/history',
                    },
                ]}
                rightAddon={
                    <Tooltip
                        title={
                            isCurrentLatest
                                ? undefined
                                : 'Send to organisation is only available on the latest version of this obligation.'
                        }
                    >
                        <span>
                            <Button
                                type="secondary"
                                onClick={() => setIsModalVisible(true)}
                                disabled={!isCurrentLatest}
                            >
                                Send to organisation
                            </Button>
                        </span>
                    </Tooltip>
                }
            />
        </>
    ) : (
        <Skeleton active />
    );
}
